import type { ReactNode } from "react";

import { isAlbumSlug } from "./content/gallery";
import { isSadguruSlug } from "./content/inspiration";
import { langFromPath } from "./i18n/lang";
import { AlbumPage } from "./pages/AlbumPage";
import { CalendarPage } from "./pages/CalendarPage";
import { ContactPage } from "./pages/ContactPage";
import { DonatePage } from "./pages/DonatePage";
import { GalleryPage } from "./pages/GalleryPage";
import { Home } from "./pages/Home";
import { InspirationPage } from "./pages/InspirationPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { SadguruPage } from "./pages/SadguruPage";
import { SwarvedPage } from "./pages/SwarvedPage";
import { ActivitiesPage } from "./pages/activities/ActivitiesPage";
import { AkhandPathPage } from "./pages/activities/AkhandPathPage";
import { DailyPracticePage } from "./pages/activities/DailyPracticePage";
import { GitaJayantiPage } from "./pages/activities/GitaJayantiPage";
import { SessionsPage } from "./pages/activities/SessionsPage";
import { WeeklySessionPage } from "./pages/activities/WeeklySessionPage";
import { ChallengePage } from "./pages/events/ChallengePage";
import { EventsPage } from "./pages/events/EventsPage";
import { IgfPage } from "./pages/events/IgfPage";
import { PayPage } from "./pages/register/PayPage";
import { RegisterPage } from "./pages/register/RegisterPage";
import { ThanksPage } from "./pages/register/ThanksPage";
import { AudioVideoPage } from "./pages/resources/AudioVideoPage";
import { ComingSoonPage } from "./pages/resources/ComingSoonPage";
import { NewsletterPage } from "./pages/resources/NewsletterPage";
import { ResourcesPage } from "./pages/resources/ResourcesPage";
import { SlideReadingPage } from "./pages/resources/SlideReadingPage";
import { SlidesPage } from "./pages/resources/SlidesPage";

export interface RouteMatch {
  name: string;
  params: Record<string, string>;
}

interface Route {
  name: string;
  pattern: RegExp;
  render: (params: Record<string, string>) => ReactNode;
}

/** Route table. Later tasks append entries; order matters for prefixes. */
// fallow-ignore-next-line unused-export -- exercised directly by hindi.test.tsx
export const ROUTES: Route[] = [
  { name: "home", pattern: /^\/$/, render: () => <Home /> },
  {
    name: "register",
    pattern: /^\/register$/,
    render: () => <RegisterPage />,
  },
  {
    name: "thanks",
    pattern: /^\/register\/thanks$/,
    render: () => <ThanksPage />,
  },
  {
    name: "pay",
    pattern: /^\/register\/pay$/,
    render: () => <PayPage />,
  },
  {
    name: "events",
    pattern: /^\/events$/,
    render: () => <EventsPage />,
  },
  {
    name: "challenge",
    pattern: /^\/events\/meditation-challenge$/,
    render: () => <ChallengePage />,
  },
  {
    name: "igf",
    pattern: /^\/events\/igf-2021$/,
    render: () => <IgfPage />,
  },
  {
    name: "contact",
    pattern: /^\/contact$/,
    render: () => <ContactPage />,
  },
  {
    name: "swarved",
    pattern: /^\/swarved$/,
    render: () => <SwarvedPage />,
  },
  {
    name: "calendar",
    pattern: /^\/calendar$/,
    render: () => <CalendarPage />,
  },
  {
    name: "donate",
    pattern: /^\/donate$/,
    render: () => <DonatePage />,
  },
  {
    name: "inspiration",
    pattern: /^\/inspiration$/,
    render: () => <InspirationPage />,
  },
  {
    name: "sadguru",
    pattern:
      /^\/inspiration\/(?<slug>sadguru-(?:sadafaldeo|dharmchandradeo|swatantradeo)-ji-maharaj)$/,
    render: (params) =>
      isSadguruSlug(params.slug) ? (
        <SadguruPage slug={params.slug} />
      ) : (
        <NotFoundPage />
      ),
  },
  {
    name: "gallery",
    pattern: /^\/gallery$/,
    render: () => <GalleryPage />,
  },
  {
    name: "gallery-album",
    pattern:
      /^\/gallery\/(?<slug>lifestyle-fair-2022|meet-greet-meditate-share-2022|old-photos)$/,
    render: (params) =>
      isAlbumSlug(params.slug) ? (
        <AlbumPage slug={params.slug} />
      ) : (
        <NotFoundPage />
      ),
  },
  {
    name: "resources",
    pattern: /^\/resources$/,
    render: () => <ResourcesPage />,
  },
  {
    name: "resources-audio-video",
    pattern: /^\/resources\/audio-video$/,
    render: () => <AudioVideoPage />,
  },
  {
    name: "resources-slide-reading",
    pattern: /^\/resources\/slide-reading$/,
    render: () => <SlideReadingPage />,
  },
  {
    name: "resources-slides",
    pattern: /^\/resources\/slides$/,
    render: () => <SlidesPage />,
  },
  {
    name: "resources-scientific-research",
    pattern: /^\/resources\/scientific-research$/,
    render: () => (
      <ComingSoonPage
        title="Scientific Research"
        suggestions={[
          { label: "Daily Practice", href: "/activities/daily-practice" },
          { label: "Audio & Video", href: "/resources/audio-video" },
        ]}
      />
    ),
  },
  {
    name: "resources-newsletter-magazine",
    pattern: /^\/resources\/newsletter-magazine$/,
    render: () => <NewsletterPage />,
  },
  {
    name: "resources-prayer-music",
    pattern: /^\/resources\/prayer-music$/,
    render: () => (
      <ComingSoonPage
        title="Prayers & Music"
        suggestions={[
          { label: "Daily Practice", href: "/activities/daily-practice" },
          { label: "Audio & Video", href: "/resources/audio-video" },
        ]}
      />
    ),
  },
  {
    name: "activities",
    pattern: /^\/activities$/,
    render: () => <ActivitiesPage />,
  },
  {
    name: "activities-daily-practice",
    pattern: /^\/activities\/daily-practice$/,
    render: () => <DailyPracticePage />,
  },
  {
    name: "activities-weekly-session",
    pattern: /^\/activities\/weekly-session$/,
    render: () => <WeeklySessionPage />,
  },
  {
    name: "activities-sessions",
    pattern: /^\/activities\/sessions$/,
    render: () => <SessionsPage />,
  },
  {
    name: "activities-swarved-akhand-path",
    pattern: /^\/activities\/swarved-akhand-path$/,
    render: () => <AkhandPathPage />,
  },
  {
    name: "activities-gita-jayanti-2021",
    pattern: /^\/activities\/gita-jayanti-2021$/,
    render: () => <GitaJayantiPage />,
  },
];

// fallow-ignore-next-line unused-export -- exercised directly by its unit test
export function matchRoute(pathname: string): RouteMatch | null {
  const { path } = langFromPath(pathname);
  for (const r of ROUTES) {
    const m = path.match(r.pattern);
    if (m) {
      return { name: r.name, params: m.groups ?? {} };
    }
  }
  return null;
}

export function Router() {
  const { path } = langFromPath(window.location.pathname);
  for (const r of ROUTES) {
    const m = path.match(r.pattern);
    if (m) {
      return <>{r.render(m.groups ?? {})}</>;
    }
  }
  return <NotFoundPage />;
}
